import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { pushModal } from 'loot-core/client/actions';
import { ApplyBudgetTemplateMode } from 'loot-core/server/budget-templates/types/template-apply-mode';
import {
  type BudgetTemplateLine,
  type Frequency,
} from 'loot-core/src/shared/budget-template';
import { integerToCurrency } from 'loot-core/src/shared/util';

import { theme } from '../../style';
import { Select } from '../common/Select';
import { View } from '../common/View';
import { Field } from '../table';

import AmountInput from './Fields/AmountInput';
import FrequencyDropdown from './Fields/FrequencyDropdown';
import StartingFromButton from './StartingFrom';
import { type CellEditing } from './types';
import { getMonthlyBudget, getYearlyBudget, VIEW_MODE } from './util';

export type IBudgetTemplateControlsProps = {
  year: string;
  budgetLine: Partial<BudgetTemplateLine>;
  months: { index: number; month: string }[];
  editingCell: CellEditing;
  viewMode: VIEW_MODE;
  onEditCell: (cell: CellEditing) => void;
  onBudgetLineChange: (budgetLine: Partial<BudgetTemplateLine>) => void;
  onBudgetLineApply: (
    budgetLine: Partial<BudgetTemplateLine>,
    mode: ApplyBudgetTemplateMode,
  ) => void;
  onBudgetAction: (month: string, action: string, arg: unknown) => void;
};

export default function BudgetTemplateControls({
  year,
  budgetLine,
  months,
  editingCell,
  onEditCell,
  onBudgetLineChange,
  onBudgetLineApply,
  onBudgetAction,
  viewMode,
}: IBudgetTemplateControlsProps) {
  const dispatch = useDispatch();
  const [localBudgetLine, setLocalBudgetLine] = useState(budgetLine);
  const isEditingThisLine = editingCell?.id === budgetLine?.category;

  // Keep row-local values in sync with external changes such as global reset,
  // while preserving the value currently being typed in this row.
  useEffect(() => {
    if (!isEditingThisLine) {
      setLocalBudgetLine(budgetLine);
    }
  }, [
    budgetLine?.amount,
    budgetLine?.category,
    budgetLine?.frequency,
    budgetLine?.starting_from,
    isEditingThisLine,
  ]);

  function onFrequencyChange(frequency: Frequency) {
    const updatedLine = { ...localBudgetLine, frequency };
    setLocalBudgetLine(updatedLine);
    onBudgetLineChange(updatedLine);
  }

  function onAmountChange(amount: number) {
    const updatedLine = { ...localBudgetLine, amount };
    setLocalBudgetLine(updatedLine);
    onBudgetLineChange(updatedLine);
  }

  function onStartingFromChange(startingFrom: string) {
    const updatedLine = { ...localBudgetLine, starting_from: startingFrom };
    setLocalBudgetLine(updatedLine);
    onBudgetLineChange(updatedLine);
  }

  return (
    <View
      style={{
        flex: 1,
        flexDirection: 'row',
        '& .hover-visible': {
          opacity: 0,
          transition: 'opacity .25s',
        },
        '&:hover .hover-visible': {
          opacity: 1,
        },
      }}
    >
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
        }}
      >
        {viewMode === VIEW_MODE.ONLY_MONTHS ? null : (
          <>
            <Field width="120" name="frequency">
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <FrequencyDropdown
                  budget={localBudgetLine}
                  isFocused={
                    editingCell &&
                    editingCell.id === localBudgetLine.category &&
                    editingCell.column === 'frequency'
                  }
                  onChange={onFrequencyChange}
                  containerStyle={{ flex: 1 }}
                />
                <StartingFromButton
                  budgetLine={localBudgetLine}
                  onChange={onStartingFromChange}
                />
              </View>
            </Field>
            <Field width="120" name="amount" data-adoption-id={'amount-input'}>
              <AmountInput
                editing={
                  editingCell &&
                  editingCell.id === localBudgetLine.category &&
                  editingCell.column === 'amount'
                }
                budgetLine={localBudgetLine}
                onEditCell={onEditCell}
                style={
                  viewMode === VIEW_MODE.COMBINED
                    ? { color: theme.tableBorderSeparator }
                    : {}
                }
                onChange={onAmountChange}
              />
            </Field>
            <View
              data-adoption-id={'budget-template-averages'}
              style={{
                flexDirection: 'row'
              }}
            >
              <Field width="120" name="monthly" style={{ textAlign: 'right' }}>
                <View
                  style={{
                    position: 'absolute',
                    top: -1,
                    left: 35,
                    width: 1,
                    height: 31,
                    backgroundColor: theme.formInputBorder,
                  }}
                />
                {integerToCurrency(getMonthlyBudget(localBudgetLine))}
              </Field>
              <Field width="120" name="yearly" style={{ textAlign: 'right' }}>
                {integerToCurrency(getYearlyBudget(localBudgetLine))}
              </Field>
            </View>

            <Field width="60" name="apply" data-adoption-id={'apply-row'} style={{ paddingRight: 10 }}>
              <Select
                options={[
                  ['apply-budget-template', 'Apply to this row'],
                  ['overwrite-budget-template', 'Overwrite this row'],
                  ['reset-category-to-zero', 'Reset row to zero'],
                ]}
                value=""
                defaultLabel={`\u2192`}
                onChange={option => {
                  if (option === 'apply-budget-template') {
                    onBudgetLineApply(
                      localBudgetLine,
                      ApplyBudgetTemplateMode.Standard,
                    );
                  } else if (option === 'overwrite-budget-template') {
                    dispatch(
                      pushModal('confirm-generic', {
                        text: `Proceeding will apply the budget template values to this row in ${year} year budget, overwriting any existing values. Are you sure you want to continue?`,
                        onConfirm: () => {
                          onBudgetLineApply(
                            localBudgetLine,
                            ApplyBudgetTemplateMode.Overwrite,
                          );
                        },
                      }),
                    );
                  } else if (option === 'reset-category-to-zero') {
                    dispatch(
                      pushModal('confirm-generic', {
                        text: `This will reset all monthly budgets for this category to zero for the entire ${year} year. Are you sure you want to continue?`,
                        onConfirm: async () => {
                          await Promise.all(
                            months.map(({ month }) =>
                              onBudgetAction(month, 'budget-amount', {
                                category: localBudgetLine.category,
                                amount: 0,
                              }),
                            ),
                          );
                        },
                      }),
                    );
                  }
                }}
              />
            </Field>
          </>
        )}
      </View>
    </View>
  );
}
